/* eslint-disable func-names */
import * as Yup from 'yup';
import dayjs from 'dayjs';
import { LETTERS_SPACE } from '../../commons';
import { REGEX_VALID_EMAIL_YUP_VALIDATION } from '../../commons/constants';
// import { isValidTaxIdNumber } from '../../commons/utils';

const isOutOfRange = [false, false, false, false];

export const birthDateValidation = (guest, guestError, I18rccl, inputDate, sailDate) => {
  const ageRange = {
    child: {
      min: 0,
      max: 12,
    },
    adult: {
      min: 13,
      max: 54,
    },
    senior: {
      min: 55,
      max: 120,
    },
  };

  let mapGuestError = { ...guestError };

  const guestAgeAtSailDateOriginal = dayjs(sailDate).diff(dayjs(guest.birthDate), 'year');
  const guestAgeAtSailDateInput = dayjs(sailDate).diff(dayjs(inputDate), 'year');

  const GUEST_SENIOR = 'senior';
  const GUEST_ADULT = 'adult';
  const GUEST_CHILD = 'child';
  const guestRange = (age) => {
    if (age >= ageRange.adult.min && age <= ageRange.adult.max) return GUEST_ADULT;
    if (age >= ageRange.child.min && age <= ageRange.child.max) return GUEST_CHILD;
    return GUEST_SENIOR;
  };

  const isValidAgeChange = (age, newAge) => {
    return (
      (guestRange(age) === GUEST_ADULT && guestRange(newAge) === GUEST_SENIOR) ||
      (guestRange(age) === GUEST_SENIOR && guestRange(newAge) === GUEST_ADULT) ||
      guestRange(age) === guestRange(newAge)
    );
  };

  // VALIDATION #1 For: _mutableFields = "age"
  isOutOfRange[guest.index - 1] = false;
  if (guest._mutableFields.includes('age')) {
    // When Age is modifiable but not your Range(child,adult,senior)
    // When: The user modifies the DOB field
    // Then: The field cannot be changed if the new “age” goes from “adult” to “child”
    // And: “Age” should be calculated in relation to the sail date, not the current date
    // And: The following error message should be displayed when trying to change to an age group that’s not allowed:
    // Message: “That age does not fall within the age group selected during booking. If you still need to make this change, please call us for assistance.”
    // Sample: guestAge:29, guestRange:Adult, guestBirthDate:1990-08-13, sailDate:2020-04-17
    if (!isValidAgeChange(guestAgeAtSailDateOriginal, guestAgeAtSailDateInput)) {
      mapGuestError = { ...guestError, ...{ birthDate: I18rccl.ageError } };
      isOutOfRange[guest.index - 1] = true;
    }
  }

  // VALIDATION #2 _mutable fields is "birthDate"
  if (guest._mutableFields.includes('birthDate') && !guest._mutableFields.includes('age')) {
    // Given: “Age” is not modifiable and “birthday” is
    // When: The user modifies the DOB field
    // Then: The user should be allowed to modify date of birth as long as the “age” does not change
    // And: “Age” should be calculated in relation to the sail date, not the current date

    // Check if trying to change age
    if (guestAgeAtSailDateInput !== guestAgeAtSailDateOriginal) {
      // Define BirthDate Error
      mapGuestError = { ...guestError, ...{ birthDate: I18rccl.birthDateError } };
    }
  }
  return mapGuestError;
};

Yup.addMethod(Yup.object, 'isRequired', function (propertyName, message) {
  return this.test('isRequired', message, function (list) {
    if (this.path === 'guests[0]' && !list.email) {
      return this.createError({
        path: `guests[0].${propertyName}`,
        message,
      });
    }

    if (this.path === 'guests[0]' && (list[propertyName] === null || list[propertyName] === '')) {
      return this.createError({
        path: `guests[0].${propertyName}`,
        message,
      });
    }
    return true;
  });
});

Yup.addMethod(Yup.object, 'isWithinRange', function (propertyName, content) {
  return this.test('isWithinRange', content, function (data) {
    if (isOutOfRange[data.index]) {
      return this.createError({
        path: `guests[${data.index}].${propertyName}`,
        message: content,
      });
    }
    return true;
  });
});

export const validationSchema = (renderContent) => {
  // pass enableTravelDocument as prop to enable traveldocument valdation
  return Yup.object({
    guests: Yup.array().of(
      Yup.object()
        .shape({
          birthDate: Yup.date(),
          email: Yup.string(renderContent('formBookingAmendFieldEmail', 'Enter your email address'))
            .nullable()
            ?.matches(
              REGEX_VALID_EMAIL_YUP_VALIDATION,
              renderContent('formBookingAmendFieldInvalidEmail', 'Enter a valid email'),
            ),
          firstName: Yup.string(
            renderContent('formBookingAmendFieldFirstName', 'Enter your first name'),
          )
            .matches(
              LETTERS_SPACE,
              renderContent(
                'formBookingAmendInvalidFormat',
                'Only characters A to Z, or spaces are allowed.',
              ),
            )
            .required(renderContent('formBookingAmendRequiredFirstName', 'First name is required')),
          gender: Yup.string(
            renderContent('formBookingAmendFieldGender', 'Select gender'),
          ).required(renderContent('formBookingAmendRequiredGender', 'Gender is required')),
          lastName: Yup.string(
            renderContent('formBookingAmendFieldLastName', 'Enter your last name'),
          )
            .matches(
              LETTERS_SPACE,
              renderContent(
                'formBookingAmendInvalidFormat',
                'Only characters A to Z, or spaces are allowed.',
              ),
            )
            .required(renderContent('formBookingAmendRequiredLastName', 'Last name is required')),
          stateCode: Yup.string(
            renderContent('formBookingAmendFieldStateCode', 'Select a state'),
          ).nullable(),
          nationality: Yup.string(),
          // **** validation for brazil travle document - temporaly dissabled ****
          // travelDocument: Yup.string().when('nationality', {
          //   is: 'BR',
          //   then: Yup.string()
          //     .nullable()
          //     .test(
          //       'required-cpf',
          //       renderContent('formBookingAmendFieldCpfRequiredMessage', 'O CPF é obrigatorio.'),
          //       (val) => !(!val && enableTravelDocument),
          //     )
          //     .test(
          //       'min',
          //       renderContent(
          //         'formBookingAmendFieldCpfLengthMessage',
          //         'O CPF deve ter onze números.',
          //       ),
          //       (val) => !(val?.length < 11 && enableTravelDocument),
          //     )
          //     .test(
          //       'cpf-validation',
          //       renderContent(
          //         'formBookingAmendFieldCpfInvalidMessage',
          //         'Por favor, digite um CPF correto.',
          //       ),
          //       (val) => {
          //         if (!enableTravelDocument) return true;
          //         return isValidTaxIdNumber(val);
          //       },
          //     ),
          // }),
          // **** validation for brazil travle document - end ****

          phoneNumber: Yup.number()
            .nullable()
            .typeError(
              renderContent('formBookingAmendFieldPhoneInvalid', 'You must specify a number'),
            )
            .min(
              111111,
              renderContent('formBookingAmendFieldPhonePlaceholder', 'Enter your phone number'),
            ),
        })
        .isRequired(
          'email',
          renderContent('formBookingAmendRequiredEmail', 'Email address is required'),
        )
        .isRequired(
          'phoneNumber',
          renderContent('formBookingAmendRequiredPhoneNumber', 'Phone number is required'),
        )
        .isWithinRange(
          'birthDate',
          renderContent(
            'formBookingAmendValidationAge',
            'That age does not fall within the age group selected during booking. If you still need to make this change, please call us for assistance.',
          ),
        ),
    ),
  });
};
